// East-West placement module.
//
// Per ssot §3.6: E-W needs TWO InstancedMesh objects per area because
// alternating panels are mirror images (east-facing vs west-facing), not just
// rotations. Mirroring via scale has gotchas with normals/lighting/back-face
// culling. So we emit:
//   area.instancedMesh — east-facing pair members (A)
//   area.ewWestMesh    — west-facing pair members (B), renderOrder + 1 to
//                        avoid z-fighting at the ridge where corners meet.
//
// Most of the structure mirrors fixedTilt.js.

import * as THREE from "three";
import { InstancedMesh2 } from "@three.ez/instanced-mesh";
import { PANEL_THICKNESS_M } from "../constants";
import { computeCompassBasis } from "./fixedTilt";
import { paintPanelEdgesBlack } from "./panel";
import { getCurrentAnchor, setCurrentAnchor } from "./common";

// ---------------------------------------------------------------------------
// Panel mesh — BoxGeometry gives panels real-world thickness so they read as
// solid modules from any angle. Edge faces are painted black via vertex colors
// so only the front/back show the panel texture. DoubleSide keeps both faces
// rendering for the rare interior view between the two E-W ridges.
// ---------------------------------------------------------------------------

const createTiltedPanelInstance = function (panelWidth, panelHeight, panelTexture, panelsCount) {
  const geometry = new THREE.BoxGeometry(panelWidth, panelHeight, PANEL_THICKNESS_M);
  paintPanelEdgesBlack(geometry);
  const material = panelTexture
    ? new THREE.MeshBasicMaterial({ map: panelTexture, vertexColors: true, transparent: true, opacity: 0.5, side: THREE.DoubleSide })
    : new THREE.MeshBasicMaterial({ color: 0x000000, transparent: true, opacity: 0.5, side: THREE.DoubleSide });
  return new InstancedMesh2(this.renderer, panelsCount, geometry, material);
};

// ---------------------------------------------------------------------------
// Apex-to-apex formula minimum (m).
//
//   A2A_min = 2 · panelHeight · cos(θ) + pairGap/100 + thickness · sin(θ)
//
// Two panel footprints back-to-back across the ridge (= 2·panelHeight·cos)
// plus the horizontal pair gap, plus a thickness-dependent term:
// `thickness · sin(θ)` accounts for the front-face poke-out of the two eaves
// edges that face each other across the row gap. Each panel's front face sits
// (t/2)·sin(θ) outward of its center plane in the pitch direction, and two
// mirror panels contribute (t/2)·sin(θ) each → t·sin(θ) of extra clearance
// needed beyond the footprint-only formula. Without this term adjacent rows
// physically intersect at the eaves when inter_gap = 0. UI clamps A2A to this
// minimum; the user adds Inter-gap on top via the sidebar.
// ---------------------------------------------------------------------------

const apexToApexFormulaMin = (panelHeightM, tiltRad, pairGapCm, thicknessM) =>
  2 * panelHeightM * Math.cos(tiltRad)
  + (pairGapCm ?? 0) / 100
  + (thicknessM ?? 0) * Math.sin(tiltRad);

// ---------------------------------------------------------------------------
// Main placement function — East-West geometry (phase 2: single-mesh).
//
// Layout per ssot §3.11–§3.12:
//   ridgeDir  = aziDir            — along the ridge axis (panels touch side-by-side)
//   pitchDir  = normalOut × ridgeDir — perpendicular to ridge, in the roof plane
//   rowPitch  = A2A (m)            — ridge-to-ridge distance (along pitchDir)
//   colPitch  = panelWidth         — along ridgeDir
//
// For each candidate ridge point, the pair is:
//   posA = ridge + pitchDir·(footH/2) + normalOut·elevBase   (east-facing)
//   posB = ridge - pitchDir·(footH/2) + normalOut·elevBase   (west-facing)
//
// Containment check requires BOTH A's and B's footprints to fit inside the
// polygon (and clear restricted areas), otherwise the pair is dropped.
//
// Phase 2: both A and B are placed in the SAME instanced mesh with east-facing
// rotation. The resulting visual will look wrong on the west side — that's
// fixed in Phase 3 by splitting B into `area.ewWestMesh`.
// ---------------------------------------------------------------------------

export const populateEastWestArea = async function (
  area,
  persist = true,
  forceFlipNormal = false,
  areaDragged = false
) {
  // Phase 1: roof plane setup (identical convention to fixedTilt).
  const vectorPoints = area.points.map((p) => p.position);
  let intoRoof;
  if (area.normal != undefined) {
    intoRoof = new THREE.Vector3(area.normal.x, area.normal.y, area.normal.z);
  } else {
    intoRoof = this.calculatePlaneNormal(vectorPoints);
    if (forceFlipNormal || !this.isAreaInFrontOfModel(areaDragged)) {
      intoRoof.negate();
    }
    if (this.normalNeedsFlipping?.(vectorPoints[0], intoRoof) && !forceFlipNormal) {
      return this.populateArea(area, false, true);
    }
  }
  const normalOut = intoRoof.clone().negate();

  const center = this.getCenterPointFromVectors(vectorPoints);
  const simulatedCamera = this.getSimulatedCamera(center, intoRoof);
  const points = this.getInnerPlanePoints(area, intoRoof, simulatedCamera);

  if (area.innerPlane) {
    this.scene.remove(area.innerPlane);
    area.innerPlane.geometry?.dispose();
    area.innerPlane.material?.dispose();
    area.innerPlane = null;
  }
  const innerPlane = this.createInnerPlane(points, area.stencilCount);
  area.innerPlane = innerPlane;
  this.scene.add(innerPlane);

  const innerPlaneEdges = points.map((p, i) => [p, points[(i + 1) % points.length]]);
  innerPlane.edges = innerPlaneEdges;

  // Phase 2: panel + roof-plane axes.
  const chosenPanel = this.getPanelById(area.panelId, area.orientation);
  if (!chosenPanel) return;
  const panelWidth   = chosenPanel.size.width;
  const panelHeight  = chosenPanel.size.height;
  const panelTexture = chosenPanel.texture;

  const rightMostPoint  = this.getRightMostPoint(points, simulatedCamera);
  const leftMostPoint   = this.getLeftMostPoint(points, simulatedCamera);
  const topMostPoint    = this.getTopMostPoint(points, simulatedCamera);
  const bottomMostPoint = this.getBottomMostPoint(points, simulatedCamera);

  const xDir = this.getAxisDirectionInPlane(simulatedCamera, new THREE.Vector3(1, 0, 0), intoRoof);
  const yDir = this.getAxisDirectionInPlane(simulatedCamera, new THREE.Vector3(0, 1, 0), intoRoof);

  const projTL = this.calculateProjectedPoint(topMostPoint,    leftMostPoint,  xDir, yDir, false, true);
  const projTR = this.calculateProjectedPoint(topMostPoint,    rightMostPoint, xDir, yDir, true,  true);
  const projBL = this.calculateProjectedPoint(bottomMostPoint, leftMostPoint,  xDir, yDir, false, false);

  const horizontalVector = projTR.clone().sub(projTL).normalize();
  const verticalVector   = projTL.clone().sub(projBL).normalize();

  // Phase 3: E-W coordinate frame.
  const ew      = area.mountingConfigurations?.east_west ?? {};
  const tiltDeg = ew.tilt ?? 10;
  const tiltRad = (tiltDeg * Math.PI) / 180;
  const cosT = Math.cos(tiltRad);
  const sinT = Math.sin(tiltRad);

  // Azimuth is measured clockwise from geographic north on the roof plane:
  //   0° = north, 90° = east, 180° = south, 270° = west.
  // The ridge axis is `aziDir` — panels run along it side-by-side, mirroring
  // about it east/west. Falls back to the polygon basis if the roof normal is
  // parallel to world north.
  const azRad   = ((ew.azimuth ?? 0) * Math.PI) / 180;
  const compass = computeCompassBasis(normalOut, this.project);
  const azBase  = compass ?? { north: verticalVector, east: horizontalVector };
  const ridgeDir = azBase.north.clone()
    .multiplyScalar(Math.cos(azRad))
    .addScaledVector(azBase.east, Math.sin(azRad))
    .normalize();
  const pitchDir = new THREE.Vector3().crossVectors(normalOut, ridgeDir).normalize();

  // East-facing panel A: face normal tilts from `normalOut` toward `+pitchDir`.
  const faceDirA = new THREE.Vector3()
    .addScaledVector(normalOut, cosT)
    .addScaledVector(pitchDir,  sinT);
  // panelUp for A — "up the slope" points opposite the face's pitch component,
  // mirroring fixedTilt's convention (rear-up, front-down).
  const panelUpA = new THREE.Vector3()
    .addScaledVector(pitchDir,  -cosT)
    .addScaledVector(normalOut,  sinT);

  // West-facing panel B: mirror of A across the ridge plane. Face normal tilts
  // toward `-pitchDir`; panelUp mirrors so the rear edge remains "up the slope"
  // on B's side.
  const faceDirB = new THREE.Vector3()
    .addScaledVector(normalOut,  cosT)
    .addScaledVector(pitchDir,  -sinT);
  const panelUpB = new THREE.Vector3()
    .addScaledVector(pitchDir,   cosT)
    .addScaledVector(normalOut,  sinT);

  // Phase 4: grid layout.
  // null apex_to_apex means formula min; a non-null value is a user override.
  const footH     = panelHeight * cosT;
  const pairGapCm = ew.pair_gap ?? 5;

  let a2aCm = ew.apex_to_apex;
  if (a2aCm == null) {
    const a2aM = apexToApexFormulaMin(panelHeight, tiltRad, pairGapCm, PANEL_THICKNESS_M);
    a2aCm = parseFloat((a2aM * 100).toFixed(1));
  }
  const rowPitch = a2aCm / 100;   // m, along pitchDir
  const colPitch = panelWidth;    // m, along ridgeDir
  const elevBase = (area.offset ?? 0) / 100 + (panelHeight * sinT) / 2;

  // Offset of each panel from the ridge point along pitchDir.
  // posA at +pitchDir·offsetFromRidge, posB at -pitchDir·offsetFromRidge.
  // The pair gap is shared at the ridge — split half to each side.
  const offsetFromRidge = footH / 2 + (pairGapCm / 100) / 2;

  // Grid extents.
  const areaWidth  = leftMostPoint.distanceTo(rightMostPoint);
  const areaHeight = bottomMostPoint.distanceTo(topMostPoint);
  const maxDim = Math.sqrt(areaWidth * areaWidth + areaHeight * areaHeight);
  const iRange = Math.ceil(maxDim / (2 * rowPitch)) + 2;
  const jRange = Math.ceil(maxDim / (2 * colPitch)) + 2;

  // Phase 5: containment + restricted-area exclusion + 8×8 sweep.
  const areaScreenData = this.buildAreaScreenData(points, innerPlaneEdges, simulatedCamera);

  const restrictedAreas = (area.restrictedAreas ?? []).map((ra) => ({
    points: ra.points.map((p) =>
      new THREE.Vector3(p.position.x, p.position.y, p.position.z)
    ),
    edges: ra.edges,
  }));

  // Half-extents along the two roof-plane axes for ONE panel's footprint.
  // The panel's long edge (panelWidth) runs along ridgeDir; the short edge's
  // footprint (footH) is along pitchDir.
  const hW = ridgeDir.clone().multiplyScalar(panelWidth / 2);
  const hH = pitchDir.clone().multiplyScalar(footH / 2);
  const ridgeOffset = pitchDir.clone().multiplyScalar(offsetFromRidge);

  const fitsAtRidgePoint = (ridgePoint) => {
    // Pair-A footprint center.
    const cA = ridgePoint.clone().add(ridgeOffset);
    const a0 = cA.clone().add(hW).add(hH);
    const a1 = cA.clone().sub(hW).add(hH);
    const a2 = cA.clone().sub(hW).sub(hH);
    const a3 = cA.clone().add(hW).sub(hH);
    const aCorners = [a0, a1, a2, a3];
    const aEdges = [[a0, a1], [a1, a2], [a2, a3], [a3, a0]];
    if (!this.isRectangleWithinPoints(aCorners, aEdges, points, innerPlaneEdges, simulatedCamera, areaScreenData)) return null;
    for (const ra of restrictedAreas) {
      if (this.isRectangleIntersectingArea(aCorners, aEdges, ra.points, ra.edges, simulatedCamera)) return null;
    }

    // Pair-B footprint center (mirror across the ridge).
    const cB = ridgePoint.clone().sub(ridgeOffset);
    const b0 = cB.clone().add(hW).add(hH);
    const b1 = cB.clone().sub(hW).add(hH);
    const b2 = cB.clone().sub(hW).sub(hH);
    const b3 = cB.clone().add(hW).sub(hH);
    const bCorners = [b0, b1, b2, b3];
    const bEdges = [[b0, b1], [b1, b2], [b2, b3], [b3, b0]];
    if (!this.isRectangleWithinPoints(bCorners, bEdges, points, innerPlaneEdges, simulatedCamera, areaScreenData)) return null;
    for (const ra of restrictedAreas) {
      if (this.isRectangleIntersectingArea(bCorners, bEdges, ra.points, ra.edges, simulatedCamera)) return null;
    }

    return { cA, cB };
  };

  // Lay out a deterministic set of pairs from a single starting position.
  // Same iteration order on save and reload → byte-stable pair list.
  const layoutFromStart = (startPos) => {
    const out = [];
    for (let i = -iRange; i <= iRange; i++) {
      for (let j = -jRange; j <= jRange; j++) {
        const ridgePoint = startPos.clone()
          .addScaledVector(ridgeDir, j * colPitch)
          .addScaledVector(pitchDir, i * rowPitch);
        const fit = fitsAtRidgePoint(ridgePoint);
        if (fit) out.push(fit);
      }
    }
    return out;
  };

  let bestPairs = [];
  let bestStart = null;

  // Sweep on first entry into this mode (no anchor yet); otherwise replay from
  // the saved anchor so a mode-switch preserves where pairs were placed/dragged.
  const existingAnchor = getCurrentAnchor(area);
  if (!existingAnchor) {
    const N_SAMPLES = 8;
    for (let si = 0; si < N_SAMPLES; si++) {
      for (let sj = 0; sj < N_SAMPLES; sj++) {
        const dRow = (rowPitch * si) / N_SAMPLES;
        const dCol = (colPitch * sj) / N_SAMPLES;
        const sweepStart = center.clone()
          .addScaledVector(pitchDir, -dRow)
          .addScaledVector(ridgeDir,  dCol);
        const candidates = layoutFromStart(sweepStart);
        if (candidates.length > bestPairs.length) {
          bestPairs = candidates;
          bestStart = sweepStart;
        }
      }
    }
    // Center fallback for single-pair areas.
    if (bestPairs.length === 0) {
      const centerFit = fitsAtRidgePoint(center);
      if (centerFit) {
        bestPairs = [centerFit];
        bestStart = center.clone();
      }
    }
    if (bestStart) setCurrentAnchor(area, bestStart.clone());
  } else {
    const savedStart = new THREE.Vector3(existingAnchor.x, existingAnchor.y, existingAnchor.z);
    bestPairs = layoutFromStart(savedStart);
    if (bestPairs.length === 0) {
      const centerFit = fitsAtRidgePoint(savedStart);
      if (centerFit) bestPairs = [centerFit];
    }
  }

  // Phase 6: build TWO instanced meshes (east-facing A + west-facing B).
  if (area.instancedMesh) {
    this.scene.remove(area.instancedMesh);
    area.instancedMesh.dispose?.();
    area.instancedMesh = null;
  }
  if (area.ewWestMesh) {
    this.scene.remove(area.ewWestMesh);
    area.ewWestMesh.dispose?.();
    area.ewWestMesh = null;
  }

  let instancedMesh, ewWestMesh;
  const pairCount = bestPairs.length;
  if (pairCount > 0) {
    instancedMesh = createTiltedPanelInstance.call(
      this, panelWidth, panelHeight, panelTexture, pairCount
    );
    ewWestMesh = createTiltedPanelInstance.call(
      this, panelWidth, panelHeight, panelTexture, pairCount
    );
    // The west panel's bottom edge meets the east panel's bottom edge at the
    // ridge. With identical world positions on that shared edge, GPU z-fighting
    // produces flicker. Nudging renderOrder gives B priority — visually clean
    // without a per-vertex polygon offset.
    ewWestMesh.renderOrder = (instancedMesh.renderOrder ?? 0) + 1;

    this.scene.add(instancedMesh);
    this.scene.add(ewWestMesh);

    const tempMatrix     = new THREE.Matrix4();
    const tempPosition   = new THREE.Vector3();
    const tempTarget     = new THREE.Vector3();
    const tempQuaternion = new THREE.Quaternion();
    const tempScale      = new THREE.Vector3(1, 1, 1);

    for (let idx = 0; idx < pairCount; idx++) {
      const { cA, cB } = bestPairs[idx];

      // Panel A → east-facing mesh.
      tempPosition.set(cA.x, cA.y, cA.z).addScaledVector(normalOut, elevBase);
      tempTarget.copy(tempPosition).sub(faceDirA);
      tempQuaternion.setFromRotationMatrix(
        new THREE.Matrix4().lookAt(tempPosition, tempTarget, panelUpA)
      );
      tempMatrix.compose(tempPosition, tempQuaternion, tempScale);
      instancedMesh.setMatrixAt(idx, tempMatrix);

      // Panel B → west-facing mesh (mirror tilt: faceDirB / panelUpB).
      tempPosition.set(cB.x, cB.y, cB.z).addScaledVector(normalOut, elevBase);
      tempTarget.copy(tempPosition).sub(faceDirB);
      tempQuaternion.setFromRotationMatrix(
        new THREE.Matrix4().lookAt(tempPosition, tempTarget, panelUpB)
      );
      tempMatrix.compose(tempPosition, tempQuaternion, tempScale);
      ewWestMesh.setMatrixAt(idx, tempMatrix);
    }
    instancedMesh.instanceMatrix.needsUpdate = true;
    ewWestMesh.instanceMatrix.needsUpdate = true;

    // Seeds for bulk-move drag (see fixedTilt.js Phase 6 comment).
    instancedMesh.getMatrixAt(0, tempMatrix);
    tempMatrix.decompose(tempPosition, tempQuaternion, tempScale);
    instancedMesh.seed = tempPosition.clone();
    ewWestMesh.getMatrixAt(0, tempMatrix);
    tempMatrix.decompose(tempPosition, tempQuaternion, tempScale);
    ewWestMesh.seed = tempPosition.clone();
  }

  // Phase 7: store on area.
  const infinitePlane = new THREE.Plane().setFromNormalAndCoplanarPoint(intoRoof, center);

  area.instancedMesh    = instancedMesh;
  area.ewWestMesh       = ewWestMesh;
  area.normal           = intoRoof;
  area.ground           = verticalVector;
  area.simulatedCamera  = simulatedCamera;
  area.verticalVector   = verticalVector;
  area.horizontalVector = horizontalVector;
  area.aziDir           = ridgeDir;   // shared name with fixedTilt for downstream consumers
  area.perpDir          = pitchDir;
  area.infinitePlane    = infinitePlane;
  area.areaCenter       = center;
  area.testPanel        = this.createTempPanel(chosenPanel);

  if (instancedMesh) {
    this.checkPanelsInSolarArea(area);
    const panelsCenter = this.calculatePanelsCenter(area);
    area.panelsCenter = panelsCenter;
    this.refreshSolarGroupLabels(area, panelsCenter, horizontalVector);
  }

  if (persist) this.createUpdateSolarGroup(area);
};